import { motion } from 'framer-motion';
import { Instagram, Linkedin, ArrowUp, Mail, Phone, MessageCircle, MapPin } from 'lucide-react';
import { companyInfo } from '../data';

const socialLinks = [
  { icon: Instagram, href: 'https://www.instagram.com/empbiz.solution?igsh=MWhqbTJzbWFjM3o4eg==', label: 'Instagram' },
  { icon: Linkedin, href: 'https://www.linkedin.com/company/emporium-solution/', label: 'LinkedIn' },
];

const quickLinks = [
  { label: 'Tentang Kami', href: '#about' },
  { label: 'Layanan', href: '#services' },
  { label: 'Tim', href: '#team' },
  { label: 'Testimoni', href: '#testimonials' },
  { label: 'Kontak', href: '#contact' },
];

const services = [
  'Business Plan',
  'Market Research',
  'Tax & Accounting',
  'Business Proposal',
  'IT Service',
  'Strategy Development',
];

export function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative bg-[#0a1628] text-white overflow-hidden">
      {/* Top Gradient Line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto section-padding py-16 lg:py-20">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg overflow-hidden">
                <img 
                  src="/emp-logo.png" 
                  alt="Emporium Solution Logo" 
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div>
                <span className="font-display font-bold text-xl">Emporium</span>
                <span className="block text-xs text-primary-400 font-medium -mt-1">Solution</span>
              </div>
            </div>

            <p className="text-dark-300 mb-6 leading-relaxed">
              Solusi dan Partner terpercaya untuk kesuksesan bisnis Anda, dengan layanan profesional untuk memecahkan setiap titik permasalahan pekerjaan Anda.
            </p>

            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    whileHover={{ scale: 1.1, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-10 h-10 bg-[#0d1d33] rounded-lg flex items-center justify-center text-blue-200 hover:bg-blue-500 hover:text-white transition-colors duration-300"
                    aria-label={social.label}
                  >
                    <Icon className="w-5 h-5" />
                  </motion.a>
                );
              })}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h4 className="font-bold text-lg mb-6">Tautan Cepat</h4>
            
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <motion.a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    whileHover={{ x: 5 }}
                    className="text-dark-400 hover:text-primary-400 transition-colors duration-300 inline-flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 bg-dark-600 rounded-full group-hover:bg-primary-500" />
                    {link.label}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4 className="font-bold text-lg mb-6">Layanan</h4>
            
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <motion.a
                    href="#services"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('#services');
                    }}
                    whileHover={{ x: 5 }}
                    className="text-dark-400 hover:text-primary-400 transition-colors duration-300 inline-flex items-center gap-2"
                  >
                    {service}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h4 className="font-bold text-lg mb-6">Kontak Kami</h4>
            
            <div className="space-y-4">
              <div className="flex gap-3">
                <MapPin className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                <p className="text-dark-400 text-sm">{companyInfo.address}</p>
              </div>
              
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex gap-3 text-dark-400 hover:text-primary-400 transition-colors"
              >
                <Phone className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <span className="text-sm">{companyInfo.phone}</span>
              </a>
              
              <a
                href={`mailto:${companyInfo.email}`}
                className="flex gap-3 text-dark-400 hover:text-primary-400 transition-colors"
              >
                <Mail className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <span className="text-sm">{companyInfo.email}</span>
              </a>
            </div>

            {/* WhatsApp CTA */}
            <motion.a
              href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              Chat WhatsApp
            </motion.a>
          </motion.div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-dark-800">
        <div className="max-w-7xl mx-auto section-padding py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-dark-500 text-sm text-center md:text-left">
              © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
            </p>

            {/* Back to Top */}
            <motion.button
              onClick={scrollToTop}
              whileHover={{ scale: 1.1, y: -3 }}
              whileTap={{ scale: 0.9 }}
              className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
              aria-label="Back to top"
            >
              <ArrowUp className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </footer>
  );
}